/**
 * Platform identity: label, colour slot, and account route.
 *
 * One table so a platform looks the same everywhere. The colour is a series
 * token, not a brand colour: brand colours are for /connect, where picking a
 * platform is the task. Here colour is just identity in a list, and the
 * series palette is the one that has been validated for colour-blind
 * separation against this canvas (see the note in styles.css).
 *
 * IMPORTANT: the platform name is always rendered as text beside the colour.
 * The five-slot palette clears the CVD target, but identity must never be
 * carried by colour alone — and that rule is what makes a five-way colour
 * split legitimate here at all.
 */
export const PLATFORMS = {
  tiktok: { label: 'TikTok', series: 'var(--color-series-1)', route: (id) => `/account/${id}` },
  instagram: { label: 'Instagram', series: 'var(--color-series-2)', route: (id) => `/ig-account/${id}` },
  facebook: { label: 'Facebook', series: 'var(--color-series-3)', route: (id) => `/fb-page/${id}` },
  threads: { label: 'Threads', series: 'var(--color-series-4)', route: (id) => `/threads-account/${id}` },
  twitter: { label: 'X', series: 'var(--color-series-5)', route: (id) => `/twitter-account/${id}` },
};

export const PLATFORM_KEYS = Object.keys(PLATFORMS);

export const platformLabel = (key) => PLATFORMS[key]?.label || key;
export const platformSeries = (key) => PLATFORMS[key]?.series || 'var(--color-ink-subtle)';
export const platformRoute = (key, id) => PLATFORMS[key]?.route?.(id) || '/dashboard';

/** 128400 -> "128.4k". Keeps a stat tile from wrapping on a phone. */
export const compact = (n) => {
  if (n === null || n === undefined) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return String(n);
};

/** Signed compact number for a delta: "+1.2k", "-340", "—" when unknown. */
export const signed = (n) => {
  if (n === null || n === undefined) return '—';
  const s = compact(Math.abs(n));
  return n > 0 ? `+${s}` : n < 0 ? `−${s}` : '0';
};

export const pct = (v) =>
  v === null || v === undefined ? null : `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(1)}%`;

/** Relative time, short form, for a timeline row. */
export const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!then || then < 86_400_000) return ''; // epoch-0 sentinel = unknown publish time
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d`;
  const months = Math.round(days / 30);
  return months < 12 ? `${months}mo` : `${Math.round(months / 12)}y`;
};
